import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTSpinner from '../common/RTSpinner';
import RTButton from '../common/RTButton';
import NoticeToVacateModal from './NoticeToVacateModal';
import RevokeNoticeModal from './RevokeNoticeModal';
import UpdateAllocationStatusModal from './UpdateAllocationStatusModal';
import EditAllocationAmountModal from './EditAllocationAmountModal';
import { apiGet } from '../../api/apiClient';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import { getStatusDisplay } from '../../utils/allocationStatusUtils';
import './ViewAllocationDetails.css';

const ViewAllocationDetails = () => {
  const { allocationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [allocation, setAllocation] = useState(null);
  const [error, setError] = useState(null);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showEditAllocationAmountModal, setShowEditAllocationAmountModal] = useState(false);
  const [statusModalMode, setStatusModalMode] = useState(null);

  useEffect(() => {
    fetchAllocationDetails();
  }, [allocationId]);

  const fetchAllocationDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiGet(`/allocation/${allocationId}`);
      setAllocation(response.data);
    } catch (error) {
      console.error('Error fetching allocation details:', error);
      setError('Failed to load allocation details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if end date is in the past
  const isEndDateInPast = (endDate) => {
    if (!endDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    return end <= today;
  };

  // Helper function to check if start date is today or in the past
  const isStartDateTodayOrPast = (startDate) => {
    if (!startDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    return start <= today;
  };

  // Helper function to check if should show Start Allocation button
  const shouldShowStartAllocation = () => {
    return allocation.status === 'FutureAllocation' && 
           isStartDateTodayOrPast(allocation.start_date);
  };

  // Helper function to check if should show End Allocation button
  const shouldShowEndAllocation = () => {
    return allocation.status === 'Allocated' && 
           allocation.end_date && 
           isEndDateInPast(allocation.end_date);
  };

  const handleNoticeToVacate = () => {
    setShowNoticeModal(true);
  };

  const handleRevokeNotice = () => {
    setShowRevokeModal(true);
  };

  const handleStartAllocation = () => {
    setStatusModalMode('start');
    setShowUpdateStatusModal(true);
  };

  const handleEndAllocation = () => {
    setStatusModalMode('end');
    setShowUpdateStatusModal(true);
  };

  const handleEditAllocationAmount = () => {
    setShowEditAllocationAmountModal(true);
  };

  const handleModalClose = () => {
    setShowNoticeModal(false);
    setShowRevokeModal(false);
    setShowUpdateStatusModal(false);
    setShowEditAllocationAmountModal(false);
    setStatusModalMode(null);
  };

  const handleModalSuccess = () => {
    // Close all modals
    setShowNoticeModal(false);
    setShowRevokeModal(false);
    setShowUpdateStatusModal(false);
    setShowEditAllocationAmountModal(false);
    setStatusModalMode(null);
    
    // Refresh the data after successful action
    fetchAllocationDetails();
  };

  const renderActionButtons = () => {
    if (!allocation) return null;
    
    const buttons = [];

    // Start Allocation button - show when status is FutureAllocation and start date is today or past
    if (shouldShowStartAllocation()) {
      buttons.push(
        <RTButton
          key="start-allocation"
          variant="primary"
          onClick={handleStartAllocation}
        >
          Start Allocation
        </RTButton>
      );
    }

    // End Allocation button - show when status is Allocated and end date is present and past
    if (shouldShowEndAllocation()) {
      buttons.push(
        <RTButton
          key="end-allocation"
          variant="danger"
          onClick={handleEndAllocation}
        >
          End Allocation
        </RTButton>
      );
    }

    // Don't show any other action buttons for inactive allocations
    if (allocation.status && (allocation.status.toLowerCase().includes('inactive') || 
        allocation.status.toLowerCase().includes('ended') || 
        allocation.status.toLowerCase().includes('allocationended'))) {
      return buttons.length > 0 ? buttons : null;
    }

    // Cancel Allocation button - show for FutureAllocation status
    if (allocation.status === 'FutureAllocation') {
      buttons.push(
        <RTButton
          key="cancel-allocation"
          variant="danger"
          onClick={() => {
            setStatusModalMode('cancel');
            setShowUpdateStatusModal(true);
          }}
        >
          Cancel Allocation
        </RTButton>
      );
      return buttons;
    }

    // Existing logic for other buttons (Notice To Vacate, Mark As Vacated, etc.)
    if (!allocation.end_date) {
      // No end date - show Notice To Vacate
      buttons.push(
        <RTButton
          key="notice-to-vacate"
          variant="primary"
          onClick={handleNoticeToVacate}
        >
          Notice To Vacate
        </RTButton>
      );
    } else if (isEndDateInPast(allocation.end_date)) {
      // End date is in past - show Revoke Notice option
      buttons.push(
        <RTButton
          key="revoke-notice"
          variant="secondary"
          onClick={handleRevokeNotice}
        >
          Revoke Notice
        </RTButton>
      );
    } else {
      // End date is in future - show Change End Date and Revoke Notice
      buttons.push(
        <RTButton
          key="revoke-notice-future"
          variant="secondary"
          onClick={handleRevokeNotice}
        >
          Revoke Notice
        </RTButton>
      );
    }

    return buttons.length > 0 ? buttons : null;
  };

  if (loading) {
    return <RTSpinner message="Loading allocation details..." fullScreen={true} />;
  }

  if (error) {
    return (
      <div className="rt-layout">
        <Menu />
        <div className="rt-main-content">
          <div className="rt-title-row">
            <h2 className="rt-title">Allocation Details</h2>
            <div className="rt-actions">
              <RTButton variant="secondary" onClick={() => window.history.back()}>
                &#8592; Back
              </RTButton>
            </div>
          </div>
          <div className="rt-error-message">
            {error}
            <RTButton 
              type="primary" 
              onClick={fetchAllocationDetails}
              style={{ marginLeft: '10px' }}
            >
              Retry
            </RTButton>
          </div>
        </div>
      </div>
    );
  }

  if (!allocation) {
    return (
      <div className="rt-layout">
        <Menu />
        <div className="rt-main-content">
          <div className="rt-error-message">
            Allocation not found.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Allocation Details</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={() => window.history.back()}>
              &#8592; Back
            </RTButton>
            {allocation.status && 
             !allocation.status.toLowerCase().includes('inactive') && 
             !allocation.status.toLowerCase().includes('ended') && 
             !allocation.status.toLowerCase().includes('allocationended') && (
              <RTButton variant="primary" onClick={handleEditAllocationAmount}>
                Edit Allocation Amount
              </RTButton>
            )}
            <div className="action-buttons">
              {renderActionButtons()}
            </div>
          </div>
        </div>

        {/* Single Card Layout with Two Columns */}
        <div className="allocation-details-card">
          <div className="card-content">
            {/* Left Column - Allocation Details */}
            <div className="detail-section">
              <h4>Summary</h4>
              <div className="detail-row">
                <label>Status:</label>
                <span>
                  <span className={`status-badge ${allocation.status.toLowerCase()}`}>
                    {getStatusDisplay(allocation.status)}
                  </span>
                </span>
              </div>
              <div className="detail-row">
                <label>Start Date:</label>
                <span>{formatDateToDDMMYYYY(allocation.start_date)}</span>
              </div>
              <div className="detail-row">
                <label>End Date:</label>
                <span>{allocation.end_date ? formatDateToDDMMYYYY(allocation.end_date) : '-'}</span>
              </div>
              <div className="detail-row">
                <label>Notice Date:</label>
                <span>{allocation.notice_date ? formatDateToDDMMYYYY(allocation.notice_date) : '-'}</span>
              </div>
              <div className="detail-row">
                <label>PG Name:</label>
                <span>
                  <a 
                    href={`/paying-guest/view/${allocation.pg_id}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="pg-link"
                  >
                    {allocation.pg_name}
                  </a>
                </span>
              </div>
              <div className="detail-row">
                <label>Building:</label>
                <span>
                  <a 
                    href={`/building/view/${allocation.building_id}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="building-link"
                  >
                    {allocation.building_name}
                  </a>
                </span>
              </div>
              <div className="detail-row">
                <label>Room Type:</label>
                <span>{allocation.room_type}</span>
              </div>
              <div className="detail-row">
                <label>Room Capacity:</label>
                <span>{allocation.room_capacity} beds</span>
              </div>
              {allocation.days_until_vacation !== null && 
               allocation.status && 
               !allocation.status.toLowerCase().includes('inactive') && 
               !allocation.status.toLowerCase().includes('ended') && (
                <div className="detail-row">
                  <label>Days Until Vacating:</label>
                  <span className={allocation.days_until_vacation < 0 ? 'overdue' : allocation.days_until_vacation <= 7 ? 'urgent' : ''}>
                    {allocation.days_until_vacation < 0 ? `${Math.abs(allocation.days_until_vacation)} days overdue` : `${allocation.days_until_vacation} days`}
                  </span>
                </div>
              )}
            </div>

            {/* Right Column - Financial Details */}
            <div className="detail-section">
              <h4>Financial Details</h4>
              
              {/* Advance Details */}
              <div className="detail-row">
                <label>Advance Amount:</label>
                <span>₹{allocation.advance_amount?.toLocaleString() || '0'}</span>
              </div>
              <div className="detail-row">
                <label>Advance Paid:</label>
                <span className={allocation.advance_paid > 0 ? 'text-success' : ''}>
                  ₹{allocation.advance_paid?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="detail-row">
                <label>Advance Remaining:</label>
                <span className="payable-amount">
                  ₹{allocation.advance_remaining?.toLocaleString() || '0'}
                </span>
              </div>
              {allocation.advance_returned && allocation.advance_returned > 0 && (
                <div className="detail-row">
                  <label>Advance Returned:</label>
                  <span className="text-info">₹{allocation.advance_returned?.toLocaleString()}</span>
                </div>
              )}
              
              <div className="detail-row" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                <label>Base Amount:</label>
                <span>₹{allocation.total_amount?.toLocaleString()}</span>
              </div>
              {allocation.discount_type && (
                <>
                  <div className="detail-row">
                    <label>Discount:</label>
                    <span>
                      {allocation.discount_type === 'percentage' ? 
                        `${allocation.discount_value}%` : 
                        `₹${allocation.discount_value?.toLocaleString()}`
                      }
                    </span>
                  </div>
                  <div className="detail-row">
                    <label>Discounted Amount:</label>
                    <span>₹{allocation.discounted_rent?.toLocaleString()}</span>
                  </div>
                </>
              )}
              <div className="detail-row">
                <label>Final Payable Amount:</label>
                <span className="payable-amount">₹{allocation.payable_amount?.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <NoticeToVacateModal
        isOpen={showNoticeModal}
        onClose={handleModalClose}
        allocation={allocation}
        onSuccess={handleModalSuccess}
      />
      
      <RevokeNoticeModal
        isOpen={showRevokeModal}
        onClose={handleModalClose}
        allocation={allocation}
        onSuccess={handleModalSuccess}
      />

      <UpdateAllocationStatusModal
        isOpen={showUpdateStatusModal}
        mode={statusModalMode}
        allocation={allocation}
        onSuccess={handleModalSuccess}
        onCancel={handleModalClose}
      />

      <EditAllocationAmountModal
        isOpen={showEditAllocationAmountModal}
        onClose={handleModalClose}
        allocation={allocation}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default ViewAllocationDetails;
